import { useEffect, useRef, useState } from "react";
import MusicIcon from "../../../../../assets/images/icons/music.svg";
import DownloadIcon from "../../../../../assets/images/icons/download.svg";
import ShareIcon from "../../../../../assets/images/icons/share-music.svg";
import MoreIcon from "../../../../../assets/images/icons/option.svg";
import { Music, Trash2, Edit3, Play, Pause, Share2, Link2, ListMusicIcon } from "lucide-react";
import { ReactComponent as MixIconSVG } from "../../../../../assets/images/icons/mix-icon.svg";
import { ReactComponent as WaveFormSVG } from "../../../../../assets/images/icons/wave.svg";
import { Tooltip } from "../../../../../components/Tooltip";
import DropdownMenu from "../../../../../components/DropdownMenu";
import { useDispatch, useSelector } from "react-redux";
import { extractFileName } from "../../../../../helper/extractFileName";
import { deleteMasterTrack, downloadMasterTrack, fetchMastersLibrary, previewData, renameMasterTrack } from "../../../../../redux/actions/audioEngineer";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Dialog from "../../../../../components/Dialogbox";
import SendFilePopup from "../../AudioEngineer/sections/SendFilePopup";
import WaveformPlayer from "../../AudioEngineer/sections/Waveform";
import { timeAgo, toMinSec } from "../../../../../helper/dataFormat";
import { getFileExtensionRegex } from "../../../../../helper/utility";
import { useLocation, useNavigate } from "react-router-dom";
import { useUpdateMixAnalysisReport } from "../../../../../queries/urstudio/mixanalyst.query";
import { showToast } from "../../../../../helper/ShowToast";
import { consumeCredits } from "../../../../../redux/actions";
import { MixAnalystPackModal } from "../../MixAnalyst/MixAnalystPackModal";
import { CreditPlanModal } from "../../../../../components/CreditPlanModal";
import StemSplitsLibrary from "../../MixEnhancer/StemSplits/StemSplitsLibrary";

export const MastersTrackTable = ({ selectedTracks, setSelectedTracks }) => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const location = useLocation()
    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [showChargeModal, setShowChargeModal] = useState(false);
    const [showCreditPlanModal, setShowCreditPlanModal] = useState(false);
    const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false)
    const [hoveredTrack, setHoveredTrack] = useState(null);
    const [isLoading, setIsLoading] = useState(false)
    const [isPlaying, setIsPlaying] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [stemTrackId, setStemTrackId] = useState(null)
    const [stemsData, setStemsData] = useState(null)
    const [showStems, setShowStems] = useState(false)
    const [selectedTrack, setSelectedTrack] = useState(null)
    const [tempName, setTempName] = useState("");
    const [renamingTrackId, setRenamingTrackId] = useState(null);
    const [deletingTrackId, setDeletingTrackId] = useState(null);
    const [forceRender, setForceRender] = useState(false);
    const [progress, setProgress] = useState({});
    const [activeTrackId, setActiveTrackId] = useState(null);
    const [reqData, setReqData] = useState(null)
    const audioRef = useRef(null);
    const progressRef = useRef({})
    const localGenre = localStorage.getItem('genre')
    const libraryData = useSelector((state) => state.audioEngineer)
    const { hasActiveSubscription, creditDetails } = useSelector((state) => state.billing);

    const handlePlay = (trackId) => {
        setActiveTrackId(trackId); // Only this track remains active
    };

    const handleCheckboxChange = (trackId) => {
        setSelectedTracks(prev => {
            if (prev.includes(trackId)) {
                return prev.filter(id => id !== trackId);
            } else {
                return [...prev, trackId];
            }
        });
    };

    // Stop any playing track when the user navigates to another page
    useEffect(() => {
        return () => {
            if (audioRef.current && !audioRef.current.paused) {
                try {
                    audioRef.current.pause();
                } catch (e) {
                    console.warn("Failed to stop library audio on page change", e);
                }
            }
        };
    }, []);

    // Stop any playing track when the route changes while the component is still mounted
    useEffect(() => {
        if (audioRef.current && !audioRef.current.paused) {
            try {
                audioRef.current.pause();
            } catch (e) {
                console.warn("Failed to stop library audio on route change", e);
            }
        }
        setIsPlaying(null);
    }, [location.pathname]);

    const getMenuItems = (track) => [
        {
            label: "Rename...",
            icon: <Edit3 size={16} />,
            onClick: () => handleRename(track),  // pass id here
        },
        {
            label: "Delete",
            icon: <Trash2 size={16} />,
            danger: true,
            onClick: () => handleDialog(track),  // pass id here
        },
    ];

    const getShareItems = (track) => [
        {
            label: "Share File",
            icon: <Share2 size={16} />,
            onClick: () => handleEmailDialog(track),  // pass id here
        },
        {
            label: "Get Link",
            icon: <Link2 size={16} />,
            onClick: () => {
                navigator.clipboard.writeText(track?.masteredUrl);
            },  // pass id here
        },
    ];

    const downloadAudioFile = (url, format, fileName) => {
        const reqBody = {
            url: url,
            format: format,
            fileName: fileName,
        }
        dispatch(downloadMasterTrack(reqBody, () => { }))
    }

    const getDownloadItems = (track) => [
        {
            label: "Download as WAV file",
            icon: <Music size={16} />,
            onClick: () => downloadAudioFile(track?.masteredUrl, "wav", track.filename),
        },
        {
            label: "Download as MP3 file",
            icon: <Music size={16} />,
            onClick: () => downloadAudioFile(track?.masteredUrl, 'mp3', track.filename),
        },
        {
            label: "Download as M4A file",
            icon: <Music size={16} />,
            onClick: () => downloadAudioFile(track?.masteredUrl, 'm4a', track.filename),
        },
        {
            label: "Download as AAC file",
            icon: <Music size={16} />,
            onClick: () => downloadAudioFile(track?.masteredUrl, 'aac', track.filename),
        },
        {
            label: "Download as OGG file",
            icon: <Music size={16} />,
            onClick: () => downloadAudioFile(track?.masteredUrl, 'ogg', track.filename),
        },
        {
            label: "Download as AIFF file",
            icon: <Music size={16} />,
            onClick: () => downloadAudioFile(track?.masteredUrl, 'aiff', track.filename),
        }
    ];

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 5; // show 5 rows per page (adjust as needed)

    const tracks = libraryData?.mastersLibrary?.data || [];
    const totalPages = Math.ceil(tracks.length / pageSize);
    const paginatedTracks = tracks.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const handleRename = (track) => {
        setEditingId(track._id);
        setTempName(track.projectName || extractFileName(track.filename));
    };

    const handleDelete = () => {
        if (!selectedTrack) return;
        setDeletingTrackId(selectedTrack._id);
        dispatch(deleteMasterTrack(toast, selectedTrack._id, setIsLoading, () => {
            setDeletingTrackId(null);
        }))
        setIsDialogOpen(!isDialogOpen)
    };

    const handleDialog = (track) => {
        setSelectedTrack(track);
        setIsDialogOpen(!isDialogOpen)
    }

    const handleEmailDialog = (track) => {
        setSelectedTrack(track);
        setIsEmailDialogOpen(!isEmailDialogOpen)
    }

    const handlePlayPause = (track) => {
        if (isPlaying === track._id) {
            audioRef.current.pause();
            setIsPlaying(null);
        } else {
            if (audioRef.current) {
                audioRef.current.pause();
            }
            audioRef.current = new Audio(track.masteredUrl);
            audioRef.current.play();
            setIsPlaying(track._id);

            // update progress in ref only
            audioRef.current.ontimeupdate = () => {
                const percent =
                    (audioRef.current.currentTime / audioRef.current.duration) * 100;
                progressRef.current[track._id] = percent;
                setForceRender((p) => !p);
            };

            // reset when track ends
            audioRef.current.onended = () => {
                setIsPlaying(null);
                progressRef.current[track._id] = 0;
                setForceRender((p) => !p);
            };
        }
    };

    const mutateMixAnalysisReport = useUpdateMixAnalysisReport()
    const { isPending } = mutateMixAnalysisReport

    const handleMixReport = (track) => {
        if (track?.isReportGenerated) {
            window.open(`/admin/urstudio/analysis-report?id=${track._id}`, '_blank')
        } else {
            if (!creditDetails?.data || creditDetails?.data?.remaining <= 3) {
                showToast("info", "You are out of AI credits. Please purchase more to continue.");
                setShowCreditPlanModal(true);
                return;
            }
            const reqBody = {
                id: track._id,
                fileLocation: track?.masteredUrl,
                musicalStyle: localGenre === 'HIPHOP_GRIME' ? 'HIP_HOP_GRIME' : localGenre,
                isMaster: true,
                isMixed: false
            }
            setReqData(reqBody)
            if (hasActiveSubscription) {
                mutateMixAnalysisReport.mutate(reqBody, {
                    onSuccess: () => {
                        dispatch(consumeCredits(3))
                        setTimeout(() => {
                            window.open(`/admin/urstudio/analysis-report?id=${track._id}`, '_blank')
                        }, 2000)
                        dispatch(fetchMastersLibrary('', setIsLoading))
                    }
                })
            } else {
                setShowChargeModal(true)
            }
        }
    }

    const handleShowStems = (track) => {
        setStemTrackId(track._id);
        setStemsData(track?.stems)
        setShowStems(true);
    }

    return (
        <>
            <tbody>
                {isPending && (
                    <tr>
                        <td colSpan="7" className="text-center text-gray-400 py-6">Generating your Master Report...</td>
                    </tr>
                )}
                {paginatedTracks?.length === 0 && (
                    <tr>
                        <td colSpan="7" className="text-center text-gray-400 py-6">No data available</td>
                    </tr>
                )}
                {(paginatedTracks || []).map((track, idx) => (
                    <tr
                        key={track._id}
                        className="masters-library-table hover:bg-[#11175c] transition-colors rounded-lg"
                        onMouseEnter={() => setHoveredTrack(track._id)}
                        onMouseLeave={() => setHoveredTrack(null)}
                    >
                        <td className="p-2">
                            <input
                                type="checkbox"
                                checked={selectedTracks?.includes(track._id)}
                                onChange={() => handleCheckboxChange(track._id)}
                                className="rounded border-gray-400 bg-transparent text-[#FA6138] focus:ring-[#FA6138] cursor-pointer"
                                style={{ backgroundColor: 'transparent' }}
                            />
                        </td>
                        <td className="p-2">
                            <div className="max-w-[300px] flex items-center gap-3">
                                <div className="bg-[#FA6138] p-2 rounded flex items-center justify-center min-w-[35px] w-[35px] h-[35px]">
                                    {isPlaying === track._id ? (
                                        // Currently playing — always show Pause (no hover required)
                                        <button onClick={() => handlePlayPause(track)}>
                                            <Pause size={20} color="#ffffff" />
                                        </button>
                                    ) : hoveredTrack === track._id ? (
                                        // Not playing but hovered — show Play
                                        <button onClick={() => handlePlayPause(track)}>
                                            <Play size={20} color="#ffffff" />
                                        </button>
                                    ) : (
                                        // Not playing, not hovered — show static Music icon
                                        <Music size={20} color="#ffffff" />
                                    )}
                                </div>
                                <div>
                                    <p className="text-white text-sm font-medium break-all">
                                        {editingId === track._id ? (
                                            <input
                                                type="text"
                                                value={tempName}
                                                autoFocus
                                                onChange={(e) => setTempName(e.target.value)}
                                                onBlur={() => {
                                                    // save changes when input loses focus
                                                    setRenamingTrackId(track._id);
                                                    dispatch(renameMasterTrack(toast, track._id, { newName: tempName }, setIsLoading, () => {
                                                        setRenamingTrackId(null);
                                                    }))
                                                    setEditingId(null);
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        setRenamingTrackId(track._id);
                                                        dispatch(renameMasterTrack(toast, track._id, { newName: tempName }, setIsLoading, () => {
                                                            setRenamingTrackId(null);
                                                        }))
                                                        setEditingId(null);
                                                    }
                                                    if (e.key === "Escape") {
                                                        setEditingId(null); // cancel rename
                                                    }
                                                }}
                                                className="bg-transparent border-b border-gray-400 text-white outline-none"
                                            />
                                        ) : renamingTrackId === track._id ? (
                                            <span className="inline-flex items-center gap-2 text-gray-300 text-sm">
                                                <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                Renaming...
                                            </span>
                                        ) : deletingTrackId === track._id ? (
                                            <span className="inline-flex items-center gap-2 text-gray-300 text-sm">
                                                <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                Deleting...
                                            </span>
                                        ) : (
                                            track.projectName || extractFileName(track.filename)
                                        )}
                                    </p>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mt-1">
                                            <span className="text-xs text-gray-300">
                                                {getFileExtensionRegex(track?.masteredUrl).toUpperCase()}
                                            </span>
                                            <span className="bg-white text-[#00067e] text-nowrap text-xs px-2 py-1 rounded-xl">
                                                {
                                                    track?.isMixEnhanced && track?.isReportGenerated ? "ENHANCED MASTER & ANALYSIS READY"
                                                        : track?.isMixEnhanced && track?.isMastered ? "ENHANCED MASTER READY"
                                                            : track?.isReportGenerated ? "MASTER & ANALYSIS READY"
                                                                : track?.isMastered && !track?.isMixEnhanced && !track?.isReportGenerated ? "MASTER READY"
                                                                    : ''
                                                }
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </td>
                        {/* <td className="p-2">
                            <img
                                src={WaveFormImage}
                                className="h-4 sm:h-6"
                                alt="Waveform"
                            />
                        </td> */}
                        {/* Waveform with Progress */}
                        <td className="p-2">
                            <div className="flex items-center gap-3 w-full">

                                {/* Icon 1 — Stems */}
                                {track.stemProcessing &&
                                    <Tooltip
                                        text={"Show Stem splits"}
                                        style={{ bg: "bg-white", text: "text-theme_clr1", width: "w-auto" }}
                                        position={idx === 0 ? "bottom" : "top"}
                                    >
                                        <div onClick={() => handleShowStems(track)}>
                                            <ListMusicIcon className="cursor-pointer shrink-0" size={30} color="#ffffff" />
                                        </div>
                                    </Tooltip>
                                }

                                {/* Icon 2 — Mix Report */}
                                <Tooltip
                                    text={track.isReportGenerated ? "Show Mix Report" : "Generate Analysis Report"}
                                    style={{ bg: "bg-white", text: "text-theme_clr1", width: "w-auto" }}
                                    position={idx === 0 ? "bottom" : "top"}
                                >
                                    <MixIconSVG
                                        onClick={() => handleMixReport(track)}
                                        className="cursor-pointer shrink-0"
                                    />
                                </Tooltip>

                                {/* Icon 3 — Waveform (takes remaining space) */}
                                <div className="relative grow min-w-24 h-12 overflow-hidden">
                                    {/* Base waveform (gray) */}
                                    <WaveFormSVG className="absolute inset-0 w-full h-full opacity-30 object-fill" />
                                    {/* Progress waveform (white overlay) */}
                                    <div
                                        className="absolute inset-0 h-full overflow-hidden transition-all duration-200"
                                        style={{ width: `${progress[idx + 1] || 0}%` }}
                                    >
                                        <WaveFormSVG className="w-full h-full object-fill" />
                                    </div>
                                </div>

                            </div>
                        </td>
                        <td className="p-2 text-white">{toMinSec(track?.duration) || "..."}</td>
                        <td className="p-2 text-gray-300">{timeAgo(track?.createdAt) || "..."}</td>
                        <td className="p-2 text-white">{track?.fileSizeHuman || "..."}</td>
                        <td className="p-2">
                            <div className="flex items-center gap-2">
                                {[MusicIcon, DownloadIcon, ShareIcon, MoreIcon].map(
                                    (icon, i) => (
                                        <Tooltip
                                            text={
                                                i === 0
                                                    ? "Master"
                                                    : i === 1
                                                        ? "Download"
                                                        : i === 2
                                                            ? "Share"
                                                            : "More Options"
                                            }
                                            style={{
                                                bg: "bg-white",
                                                text: "text-theme_clr1",
                                                width: "w-auto",
                                            }}
                                            position={idx === 0 ? "bottom" : i === 2 ? "top-right" : "top"}
                                        >
                                            {i === 3 ? (
                                                <DropdownMenu
                                                    button={
                                                        <button className="bg-white flex items-center justify-center rounded-full hover:bg-theme_clr2 h-[30px] w-[30px]">
                                                            <img src={icon} alt="" className="block" />
                                                        </button>
                                                    }
                                                    items={getMenuItems(track)}
                                                />
                                            ) : i === 1 ? (
                                                <DropdownMenu
                                                    button={
                                                        <button className="bg-white flex items-center justify-center rounded-full hover:bg-theme_clr2 h-[30px] w-[30px]">
                                                            <img src={icon} alt="" className="block" />
                                                        </button>
                                                    }
                                                    items={getDownloadItems(track)}
                                                />
                                            ) : i === 2 ? (
                                                <DropdownMenu
                                                    button={
                                                        <button className="bg-white flex items-center justify-center rounded-full hover:bg-theme_clr2 h-[30px] w-[30px]">
                                                            <img src={icon} alt="" className="block" />
                                                        </button>
                                                    }
                                                    items={getShareItems(track)}
                                                />
                                            ) : (
                                                <button
                                                    onClick={() => {
                                                        navigate('/admin/urstudio/audio-engineer')
                                                        dispatch(previewData({ ...track, previewUrl: track?.masteredUrl, isMastered: true, playNow: true }))
                                                    }}
                                                    key={i}
                                                    className="bg-white flex items-center justify-center rounded-full hover:bg-theme_clr2 h-[30px] w-[30px]"
                                                >
                                                    <img src={icon} alt="" className="block" />
                                                </button>
                                            )}
                                        </Tooltip>
                                    )
                                )}
                            </div>
                        </td>
                    </tr>
                ))}
            </tbody>
            {/* Pagination Controls */}
            {totalPages > 1 && (
                <tfoot>
                    <tr>
                        <td colSpan="7" className="p-4">
                            <div className="flex justify-center items-center gap-3 text-white">
                                <button
                                    className="px-3 py-1 bg-theme_clr1 rounded disabled:opacity-50"
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage((p) => p - 1)}
                                >
                                    Previous
                                </button>

                                {[...Array(totalPages)].map((_, i) => (
                                    <button
                                        key={i}
                                        className={`px-3 py-1 rounded ${currentPage === i + 1 ? "bg-theme_clr2 text-white" : "bg-theme_clr1"
                                            }`}
                                        onClick={() => setCurrentPage(i + 1)}
                                    >
                                        {i + 1}
                                    </button>
                                ))}

                                <button
                                    className="px-3 py-1 bg-theme_clr1 rounded disabled:opacity-50"
                                    disabled={currentPage === totalPages}
                                    onClick={() => setCurrentPage((p) => p + 1)}
                                >
                                    Next
                                </button>
                            </div>
                        </td>
                    </tr>
                </tfoot>
            )}
            <Dialog title={"Delete Confirmation"} isOpen={isDialogOpen} hasActiveSubscription={hasActiveSubscription} onClose={handleDialog} onSave={handleDelete} leftButton={'No'} rightButton={'Yes'}>
                <div className="mb-6">
                    <h3 className="font-medium text-theme_clr2 mb-3">
                        Are you sure, you want to delete this track?
                    </h3>
                    <div className="bg-theme_clr1 text-gray-300 p-4 rounded-xl flex flex-col gap-3">
                        <p>
                            It will be removed from database permanently and cannot be restored.
                        </p>
                    </div>
                </div>
            </Dialog>
            <Dialog
                title={""}
                isOpen={showStems}
                hasActiveSubscription={false}
                onClose={() => setShowStems(false)}
                onSave={() => { }}
                needSpace={false}
                whiteCrossButton
            >
                <StemSplitsLibrary
                    addNew={() => { }}
                    handleSettings={() => { }}
                    trackId={stemTrackId}
                    data={stemsData}
                />
            </Dialog>
            <SendFilePopup isOpen={isEmailDialogOpen} onClose={() => setIsEmailDialogOpen(false)} fileUrl={selectedTrack?.masteredUrl} />
            <MixAnalystPackModal
                isOpen={showChargeModal}
                onClose={() => setShowChargeModal(false)}
                requestData={reqData}
                setMixReport={() => { }}
                setIsReportGenerated={() => { }}
                fromLibrary={true}
            />
            <CreditPlanModal
                isOpen={showCreditPlanModal}
                onClose={() => setShowCreditPlanModal(false)}
            />
        </>
    );
};